import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { awardPoints, awardAiPoints } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Gift, Check, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const QUICK = [10, 25, 50, 100, 200];
const AI_QUICK = [5, 10, 25, 50, 100];

export default function PointsAwardModal({ open, onOpenChange, recipients = [], onAwarded }) {
  const { user } = useAuth();
  const recId = user?.user_record_id || user?.record_id;

  const [amount, setAmount] = useState("");
  const [aiAmount, setAiAmount] = useState("");
  const [saving, setSaving] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!open) return;
    setAmount("");
    setAiAmount("");
    setResults([]);
  }, [open]);

  const single = recipients.length === 1 ? recipients[0] : null;
  const who = single ? single.full_name || single.first_name || "this customer" : `${recipients.length} selected customers`;

  async function run(kind) {
    const isAi = kind === "ai";
    const amt = isAi ? Math.round(Number(aiAmount) || 0) : Math.round((Number(amount) || 0) * 100) / 100;
    if (!amt || amt <= 0) {
      toast.error("Enter an amount greater than 0");
      return;
    }
    setSaving(kind);
    setResults([]);
    const out = [];
    for (const r of recipients) {
      const name = r.full_name || r.first_name || "Customer";
      try {
        const res = isAi ? await awardAiPoints(recId, r.id, amt) : await awardPoints(recId, r.id, amt);
        out.push({ name, kind, ...res });
      } catch (e) {
        out.push({ name, kind, error: e?.response?.data?.detail || "Failed" });
      }
    }
    setResults(out);
    setSaving("");
    const ok = out.filter((o) => o.success).length;
    if (ok) {
      toast.success(isAi ? `Added ${amt} AI points to ${ok} dealer${ok === 1 ? "" : "s"}` : `Awarded $${amt.toFixed(2)} to ${ok} customer${ok === 1 ? "" : "s"}`);
      onAwarded?.();
    } else {
      toast.error("Nothing awarded");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="points-award-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift size={18} className="text-amber-600" /> Award points
          </DialogTitle>
          <DialogDescription className="text-xs">
            Reward points are gift credit {who} can spend at checkout. AI points top up their video, email and AI-helper budget.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid="points-reward-section">
            <div className="text-xs font-semibold text-amber-900 mb-1 flex items-center gap-1"><Gift size={12} /> Reward points (USD)</div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="h-10 rounded-lg pl-6 bg-white"
                data-testid="points-award-amount"
              />
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {QUICK.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  className="text-xs font-semibold rounded-full border border-amber-200 bg-white text-amber-800 px-3 py-1 hover:bg-amber-100 transition-colors"
                  data-testid={`points-quick-${q}`}
                >
                  +${q}
                </button>
              ))}
            </div>
            <Button
              onClick={() => run("reward")}
              disabled={!!saving || !amount}
              className="w-full mt-2 rounded-lg h-10 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="points-award-btn"
            >
              {saving === "reward" ? <Loader2 className="animate-spin mr-1" size={14} /> : <Gift size={14} className="mr-1" />}
              {recipients.length > 1 ? `Award to ${recipients.length} customers` : "Award reward points"}
            </Button>
          </section>

          <section className="rounded-xl border border-violet-200 bg-violet-50/50 p-3" data-testid="points-ai-section">
            <div className="text-xs font-semibold text-violet-900 mb-1 flex items-center gap-1"><Sparkles size={12} /> AI points</div>
            <div className="flex items-center gap-2">
              <select
                value={aiAmount}
                onChange={(e) => setAiAmount(e.target.value)}
                className="h-10 flex-1 rounded-lg border border-violet-200 bg-white px-3 text-sm"
                data-testid="ai-points-picker"
              >
                <option value="">Pick an amount…</option>
                {AI_QUICK.map((q) => (
                  <option key={q} value={q}>+{q} AI points</option>
                ))}
              </select>
              <Input
                type="number"
                min="1"
                step="1"
                value={aiAmount}
                onChange={(e) => setAiAmount(e.target.value)}
                placeholder="custom"
                className="h-10 w-24 rounded-lg bg-white"
                data-testid="ai-points-amount"
              />
            </div>
            <Button
              onClick={() => run("ai")}
              disabled={!!saving || !aiAmount}
              className="w-full mt-2 rounded-lg h-10 bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="ai-points-award-btn"
            >
              {saving === "ai" ? <Loader2 className="animate-spin mr-1" size={14} /> : <Sparkles size={14} className="mr-1" />}
              {recipients.length > 1 ? `Add AI points to ${recipients.length} dealers` : "Add AI points"}
            </Button>
          </section>

          {results.length > 0 && (
            <div className="rounded-xl border border-zinc-200 divide-y divide-zinc-100" data-testid="points-award-results">
              {results.map((r, i) => (
                <div key={i} className="px-3 py-2 text-sm">
                  {r.error ? (
                    <div className="flex items-center gap-2 text-rose-600">
                      <AlertCircle size={14} /> {r.name}: {r.error}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-emerald-700 font-medium">
                        <Check size={14} /> {r.name}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {r.kind === "ai" ? (
                          <><span className="line-through">{r.previous_balance}</span> → <b className="text-zinc-800">{r.ai_points_balance} AI pts</b></>
                        ) : (
                          <><span className="line-through">${(r.previous_balance || 0).toFixed(2)}</span> → <b className="text-zinc-800">{r.points_display}</b></>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
